import { flash } from "@xola/ui-kit";
import type { RowClassParams } from "ag-grid-community";
import { useMemoizedFn, useMount, useUnmount } from "ahooks";
import omit from "lodash/omit";
import React, { useMemo, useState } from "react";
import { InventoryRow, Merchandise, Report } from "types";
import shallow from "zustand/shallow";
import { CustomMerchandiseProductFilter } from "../CustomFilters/CustomProductFilter";
import { CustomReportFilter } from "../CustomReportFilter";
import { CustomReportGrid } from "../CustomReportGrid";
import { ReportFilterOperator, ReportFilterState, TextFilterParams } from "../ReportFilterState";
import { ReportFilterTagList } from "../ReportFilterTagList";
import { ReportColumnDef, ReportColumnFilterValueType, useSuggestedReports } from "../SuggestedReports";
import { MerchandiseInventoryProductCell } from "./MerchandiseInventoryProductCell";
import { ColumnLayout } from "@/components/Layout/ColumnLayout";
import { useInventoryLots } from "@/hooks/resources/useInventoryLots";
import { useAutoFetchCollection } from "@/hooks/useAutoFetchCollection";
import { isInventoryTracked, MERCHANDISE_API_PATH } from "@/models/Merchandise";
import { getPendingRangeFilterKey, getReportFilterState } from "@/models/Report";
import { getQueryParams } from "@/models/ReportFilterState";
import { useCustomReportStore } from "@/stores/customReportFilterStore";

const getRowClass = (params: RowClassParams) => (params.data?.isDeleted ? "opacity-50" : undefined);

const getColumnDefs = (report: Report): ReportColumnDef[] => [
    {
        field: "product",
        headerName: "Product",
        cellRenderer: MerchandiseInventoryProductCell,
        filter: CustomMerchandiseProductFilter,
        filterParams: { report, trackedOnly: true },
        flex: 3,
        minWidth: 400,
        suppressAutoSize: true,
        sortable: true,
        comparator: (_a, _b, nodeA, nodeB) =>
            (nodeA.data?.productName ?? "").localeCompare(nodeB.data?.productName ?? ""),
    },
    {
        field: "sku",
        headerName: "SKU",
        filter: "agTextColumnFilter",
        filterParams: TextFilterParams,
        filterValueType: ReportColumnFilterValueType.STRING,
        suppressAutoSize: true,
        flex: 2,
        minWidth: 380,
        sortable: true,
        comparator: (a: string, b: string) => (a ?? "").localeCompare(b ?? ""),
    },
    {
        field: "unavailable",
        headerName: "Unavailable",
        type: "number",
        filter: false,
        flex: 1,
        minWidth: 200,
        suppressAutoSize: true,
    },
    {
        field: "committed",
        headerName: "Committed",
        type: "number",
        filter: false,
        flex: 1,
        minWidth: 200,
        suppressAutoSize: true,
    },
    {
        field: "available",
        headerName: "Available",
        type: "number",
        flex: 1,
        minWidth: 200,
        suppressAutoSize: true,
    },
    {
        field: "onHand",
        headerName: "On Hand",
        type: "number",
        filter: false,
        flex: 1,
        minWidth: 200,
        suppressAutoSize: true,
    },
];
interface Props {
    report: Report;
}

// `available` is a JS-computed aggregate (summed across lots), so it can only be filtered client-side
// (kept out of the store filter, never sent to the API). `sku` is a real catalogItem field: it goes
// through the store's currentFilter like every other report's column filter, so it is sent to the API
// (whole dataset filtered, not just the loaded page) and flows into the export request automatically.
const CLIENT_SIDE_FILTER_KEYS = new Set<keyof InventoryRow>(["available"]);
const API_FILTER_KEYS = new Set(["product", "context", "available", "sku"]);

export const MerchandiseInventoryReport = ({ report }: Props) => {
    const [excludedColumns, setExcludedColumns] = useState<string[]>(report.excludedColumns);

    const suggestedReports = useSuggestedReports();
    const hiddenFields = useMemo(
        () =>
            report.hiddenFields ??
            suggestedReports.find((suggestedReport) => suggestedReport.report.object === report.object)?.report
                .hiddenFields ??
            [],
        [report, suggestedReports],
    );

    const [initialFilter, setInitialFilter, currentFilter, setCurrentFilter, cleanFilter] = useCustomReportStore(
        (state) => [
            state.initialFilter,
            state.setInitialFilter,
            state.currentFilter,
            state.setCurrentFilter,
            state.cleanFilter,
        ],
        shallow,
    );

    useMount(() => {
        const initial = omit(getReportFilterState(report), "createdAt");
        setInitialFilter(initial);
        setCurrentFilter(initial);
    });
    useUnmount(() => cleanFilter());

    const { data: merchandises = [] } = useAutoFetchCollection<Merchandise>(MERCHANDISE_API_PATH);
    const trackedMerchandises = useMemo(
        () => merchandises.filter((merchandise) => isInventoryTracked(merchandise)),
        [merchandises],
    );

    const selectedProductIds = useMemo(
        () => (currentFilter.product?.value as string[] | undefined) ?? [],
        [currentFilter],
    );
    const selectedProducts = useMemo(
        () => trackedMerchandises.filter((m) => selectedProductIds.includes(m.id)),
        [trackedMerchandises, selectedProductIds],
    );
    const [columnFilter, setColumnFilter] = useState<ReportFilterState>({});
    // Same generic `field[operator]=value` conversion every other report uses for its column filters.
    const skuParam = useMemo(() => {
        const value = typeof currentFilter.sku?.value === "string" ? currentFilter.sku.value.trim() : "";
        return value ? getQueryParams({ sku: currentFilter.sku }) : undefined;
    }, [currentFilter.sku]);

    const {
        data: lots,
        isLoading,
        hasNextPage,
        fetchNextPage,
        isFetchingNextPage,
        refetch,
    } = useInventoryLots(selectedProducts, trackedMerchandises, undefined, skuParam);

    const columnDefs = useMemo(() => getColumnDefs(report), [report]);
    const selectedColumns = useMemo(
        () => columnDefs.filter((col) => !excludedColumns.includes(col.field)),
        [columnDefs, excludedColumns],
    );

    const handleFilterChange = useMemoizedFn((filter: ReportFilterState) => {
        // API-relevant keys (product, context, sku) go into the store's currentFilter, which drives
        // both the server fetch and the export request.
        const apiFilter = Object.fromEntries(
            Object.entries(filter).filter(([key]) => API_FILTER_KEYS.has(key)),
        ) as ReportFilterState;
        setCurrentFilter(apiFilter);

        // `available` is client-side only (JS-computed aggregate); filter it against the loaded lots in JS.
        const newColumnFilter = Object.fromEntries(
            Object.entries(filter).filter(([key]) => CLIENT_SIDE_FILTER_KEYS.has(key as keyof InventoryRow)),
        ) as ReportFilterState;
        setColumnFilter(newColumnFilter);
    });

    const filteredLots = useMemo(() => {
        if (Object.keys(columnFilter).length === 0) return lots;
        return lots.filter((row) =>
            Object.entries(columnFilter).every(([field, filterDef]) => {
                if (!filterDef) return true;
                const rowValue = row[field as keyof InventoryRow];
                const filterValue = filterDef.value;
                if (typeof rowValue === "string" && typeof filterValue === "string") {
                    const hay = rowValue.toLowerCase();
                    const needle = filterValue.toLowerCase();
                    switch (filterDef.operator) {
                        case ReportFilterOperator.CONTAINS:
                            return hay.includes(needle);
                        case ReportFilterOperator.EQ:
                            return hay === needle;
                        case ReportFilterOperator.STARTS_WITH:
                            return hay.startsWith(needle);
                        default:
                            return true;
                    }
                }
                const numberValue = rowValue as number;
                const numberFilter = filterValue as number;
                switch (filterDef.operator) {
                    case ReportFilterOperator.EQ:
                        return numberValue === numberFilter;
                    case ReportFilterOperator.NE:
                        return numberValue !== numberFilter;
                    case ReportFilterOperator.GT:
                        return numberValue > numberFilter;
                    case ReportFilterOperator.GTE:
                        return numberValue >= numberFilter;
                    case ReportFilterOperator.LT:
                        return numberValue < numberFilter;
                    case ReportFilterOperator.LTE:
                        return numberValue <= numberFilter;
                    default:
                        return true;
                }
            }),
        );
    }, [lots, columnFilter]);

    const handleRunReport = useMemoizedFn(() => {
        const pendingRangeKey = getPendingRangeFilterKey(currentFilter);
        if (pendingRangeKey) {
            const formattedString = pendingRangeKey
                .replace(/([A-Z])/g, " $1")
                .replace(/^./, (char) => char.toUpperCase());
            flash.danger(`An invalid value was specified for ${formattedString}`);
            return;
        }

        if (currentFilter === initialFilter) {
            refetch();
            return;
        }

        setInitialFilter(currentFilter);
    });

    return (
        <>
            <div className="border-b border-gray-light">
                <CustomReportFilter
                    isFetching={isLoading || isFetchingNextPage}
                    report={report}
                    dateFields={[]}
                    isFilterChanged={currentFilter !== initialFilter}
                    columns={columnDefs}
                    excludedColumns={excludedColumns}
                    onFilterChange={handleFilterChange}
                    onColumnChange={setExcludedColumns}
                    onRun={handleRunReport}
                />
                <ReportFilterTagList
                    colDefs={columnDefs}
                    value={{ ...currentFilter, ...columnFilter }}
                    hiddenFields={hiddenFields}
                    onChange={handleFilterChange}
                />
            </div>
            <ColumnLayout.Fill>
                {(height) => (
                    <div className="py-2 px-6">
                        <CustomReportGrid
                            height={height - 32}
                            report={report}
                            columnDefs={selectedColumns}
                            values={filteredLots}
                            defaultFilter={initialFilter}
                            filter={currentFilter}
                            hasMore={hasNextPage}
                            isLoading={isLoading}
                            isLoadingMore={isFetchingNextPage}
                            getRowClass={getRowClass}
                            onLoadMore={fetchNextPage}
                            onFilterChange={handleFilterChange}
                        />
                    </div>
                )}
            </ColumnLayout.Fill>
        </>
    );
};
